import React from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { SocialProfile, Platform } from '@/types/social'

interface ProfileSelectorProps {
  profiles: SocialProfile[]
  selectedProfile: string
  onProfileChange: (profileId: string) => void
  placeholder?: string
}

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

export default function ProfileSelector({ 
  profiles, 
  selectedProfile, 
  onProfileChange,
  placeholder = 'All Profiles'
}: ProfileSelectorProps) {
  const selectedProfileData = profiles.find(p => p.id === selectedProfile)

  const getProfileDisplayName = (profile: SocialProfile) => {
    return profile.display_name || profile.name
  }

  const getConnectionStatusBadge = (profile: SocialProfile) => {
    const statusConfig = {
      connected: { label: 'Connected', variant: 'default' as const },
      disconnected: { label: 'Disconnected', variant: 'destructive' as const },
      expired: { label: 'Expired', variant: 'secondary' as const },
      error: { label: 'Error', variant: 'destructive' as const }
    }

    const config = statusConfig[profile.connection_status] || statusConfig.disconnected

    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-48 justify-between">
          <div className="flex items-center gap-2">
            {selectedProfileData ? (
              <>
                <div className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[selectedProfileData.platform]}`} />
                <span className="truncate">{getProfileDisplayName(selectedProfileData)}</span>
              </>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem 
          onClick={() => onProfileChange('all')}
          className="flex items-center justify-between"
        >
          <span>{placeholder}</span>
          {selectedProfile === 'all' && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        
        {profiles.length === 0 ? (
          <DropdownMenuItem disabled>
            No profiles configured
          </DropdownMenuItem>
        ) : (
          profiles.map(profile => (
            <DropdownMenuItem
              key={profile.id}
              onClick={() => onProfileChange(profile.id)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${PLATFORM_COLORS[profile.platform]}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{getProfileDisplayName(profile)}</span>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {profile.platform}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground truncate">
                      {profile.franchise_region}
                    </span>
                    {getConnectionStatusBadge(profile)}
                  </div>
                  {profile.follower_count && (
                    <span className="text-xs text-muted-foreground">
                      {profile.follower_count.toLocaleString()} followers
                    </span>
                  )}
                </div>
              </div>
              {selectedProfile === profile.id && <Check className="h-4 w-4 flex-shrink-0" />}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}